import { FC } from 'react';
import {
  DATA_ROOM_PERMISSION_LOCKED,
  DataRoomAccessPermission,
  effectiveDataRoomPermission,
} from 'shared';
import { twMerge } from 'tailwind-merge';

import { ChevronDownIcon } from '../../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../../components/app-formatted-message/index';
import {
  Select,
  SelectButton,
  SelectOption,
  SelectOptions,
} from '../../../../../components/select/index';
import { StringKey } from '../../../../../lang/index';

export const PERMISSION_LABEL: Record<DataRoomAccessPermission, StringKey> = {
  [DataRoomAccessPermission.VIEWER]: 'VIEWER',
  [DataRoomAccessPermission.VIEWER_DOWNLOAD]: 'VIEWER_DOWNLOAD',
};

const PERMISSION_ORDER: DataRoomAccessPermission[] = [
  DataRoomAccessPermission.VIEWER,
  DataRoomAccessPermission.VIEWER_DOWNLOAD,
];

export type PermissionSelectProps = {
  isDisabled?: boolean;
  onChange: (permission: DataRoomAccessPermission) => void;
  value: DataRoomAccessPermission;
};

/**
 * While the permission lock is on the control is read-only for everybody, and it shows the
 * permission the room actually grants rather than the one stored under it — the rows the server
 * returns are already effective, and this keeps the two agreeing wherever a caller passes a
 * stored value from somewhere else.
 */
export const PermissionSelect: FC<PermissionSelectProps> = ({
  isDisabled: isDisabledProp,
  onChange,
  value: storedValue,
}) => {
  const isDisabled = isDisabledProp || DATA_ROOM_PERMISSION_LOCKED;
  const value = effectiveDataRoomPermission(storedValue);

  return (
    // The cell is a flex row, so whatever sits in it is a flex item and takes the width of its own
    // contents unless told otherwise — which made the control grow and shrink with the permission
    // on it. This wrapper is the flex item instead, and it fills the cell whatever the label says.
    <div className="w-full">
      <Select<DataRoomAccessPermission> disabled={isDisabled} onChange={onChange} value={value}>
        {({ open }: { open: boolean }) => (
          <div className="relative w-full">
            <SelectButton
              className={twMerge(
                // `w-full`: one width whatever the label says. Padding, gap and chevron take 48 of
                // it, and "Viewer + Download" needs 112 more, which is why the cell around this is
                // 230 and not the 192 the design drew — at 192 the longest of two labels ellipsed.
                'flex w-full items-center justify-between rounded border border-gray-100 py-[6px] pl-3 pr-2',
                isDisabled
                  ? 'cursor-default bg-gray-50'
                  : 'bg-white shadow-[0px_1px_1px_0px_rgba(16,24,40,0.05)]',
              )}
            >
              {/* One line, as drawn: at full width the label has room to spare, and `truncate` only
                  ever comes into play if the row is squeezed — an ellipsis reads better there than a
                  second line pushing the control out of its 32px. */}
              <span
                className={twMerge(
                  'min-w-0 truncate text-label-md font-medium',
                  isDisabled ? 'text-gray-400' : 'text-gray-700',
                )}
              >
                <AppFormattedMessage id={PERMISSION_LABEL[value]} />
              </span>
              <span className="flex size-5 shrink-0 items-center justify-center">
                <ChevronDownIcon
                  className={twMerge(
                    'size-[13px] shrink-0 transition-transform duration-200',
                    open && 'rotate-180',
                  )}
                  iconColor={isDisabled ? '#D0D5DD' : '#344054'}
                />
              </span>
            </SelectButton>
            {/* `portal` is not optional here, whatever it looks like on a page. `anchor` measures
                the button against the viewport, so without a portal the list inherits the dialog's
                `-translate-y-1/2` and lands that far down the screen; and left in the flow it is cut
                off by the modal's own clipping. Out of the flow it has to clear the sheet's z-51. */}
            {!isDisabled && (
              <SelectOptions
                anchor={{ gap: 4, to: 'bottom end' }}
                className="pointer-events-auto z-[60] mt-0 min-w-40 rounded border border-gray-100 bg-white shadow-lg"
                portal
              >
                {PERMISSION_ORDER.map((permission) => (
                  <SelectOption key={permission} value={permission}>
                    <span className="block px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50">
                      <AppFormattedMessage id={PERMISSION_LABEL[permission]} />
                    </span>
                  </SelectOption>
                ))}
              </SelectOptions>
            )}
          </div>
        )}
      </Select>
    </div>
  );
};
